import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, BedDouble, Sparkles, Loader2 } from "lucide-react";

const OP = {
  READY: { label: "Готова", cls: "bg-emerald-100 text-emerald-800" },
  AVAILABLE: { label: "Свободна", cls: "bg-emerald-100 text-emerald-800" },
  OCCUPIED: { label: "Заета", cls: "bg-amber-100 text-amber-800" },
  DIRTY: { label: "За почистване", cls: "bg-rose-100 text-rose-800" },
  ARRIVING_TODAY: { label: "Пристига днес", cls: "bg-blue-100 text-blue-800" },
  DEPARTING_TODAY: { label: "Заминава днес", cls: "bg-purple-100 text-purple-800" },
};

const empty = { number: "", name: "", room_type: "STANDARD", view: "", description: "", max_adults: 2, max_children: 1, max_babies: 1, base_price: 60, photos: [], amenities: [], active: true, op_status: "READY" };

export default function Rooms() {
  const { user } = useAuth();
  const isOwner = user.role === "OWNER";
  const [rooms, setRooms] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const genDesc = async () => {
    if (!dlg?.name && !dlg?.number) return toast.error("Въведете име/номер първо");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/generate-description", {
        kind: "room", name: dlg.name || `Стая ${dlg.number}`, name_bg: dlg.name,
        context: `${dlg.room_type === "APARTMENT" ? "апартамент" : "стандартна стая"}, ${dlg.view || ""}, до ${dlg.max_adults} възрастни`,
      });
      setDlg({ ...dlg, description: data.description_bg || data.description });
      toast.success("Описанието е генерирано");
    } catch (e) { toast.error(apiError(e)); } finally { setAiBusy(false); }
  };

  const load = () => api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (r, op_status) => {
    try { await api.put(`/rooms/${r.id}/op-status`, { op_status }); toast.success("Статусът е обновен"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    const r = dlg;
    if (!r.number || !r.name) return toast.error("Номер и име са задължителни");
    try {
      const payload = { ...r, base_price: Number(r.base_price), max_adults: Number(r.max_adults), max_children: Number(r.max_children), max_babies: Number(r.max_babies),
        amenities: typeof r.amenities === "string" ? r.amenities.split(",").map((x) => x.trim()).filter(Boolean) : r.amenities };
      if (r.id) await api.put(`/rooms/${r.id}`, payload); else await api.post("/rooms", payload);
      toast.success("Записано"); setDlg(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Изтриване на стаята?")) return; await api.delete(`/rooms/${id}`); load(); };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-4xl text-forest-deep">Стаи</h1><div className="gold-rule w-24 my-3" /></div>
        {isOwner && <Button onClick={() => setDlg({ ...empty })} className="rounded-xl gap-2 bg-forest text-white" data-testid="add-room-btn"><Plus className="w-4 h-4" /> Нова стая</Button>}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {rooms.map((r) => {
          const op = OP[r.op_status] || OP.READY;
          return (
            <div key={r.id} className="bg-white rounded-2xl border border-gold/10 shadow-soft p-5" data-testid={`room-card-${r.number}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BedDouble className="w-5 h-5 text-gold-dark" />
                  <span className="font-display text-2xl text-forest-deep">{r.number}</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-lg ${op.cls}`} data-testid={`room-status-${r.number}`}>{op.label}</span>
              </div>
              <div className="font-medium text-forest-deep mt-1">{r.name}</div>
              <div className="text-xs text-muted-foreground">{r.room_type === "APARTMENT" ? "Апартамент" : "Стандартна"} · {r.view}</div>
              <div className="text-sm text-muted-foreground mt-2">До {r.max_adults} възр. · {money(r.base_price)}/нощ</div>
              <div className="flex flex-wrap gap-1 mt-2">{(r.amenities || []).slice(0, 4).map((a) => <span key={a} className="text-[10px] bg-cream-muted px-2 py-0.5 rounded">{a}</span>)}</div>
              <div className="gold-rule my-3" />
              <div className="flex flex-wrap gap-2">
                <Select value={r.op_status} onValueChange={(v) => setStatus(r, v)}>
                  <SelectTrigger className="h-9 text-xs flex-1" data-testid={`room-op-${r.number}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(OP).map((k) => <SelectItem key={k} value={k}>{OP[k].label}</SelectItem>)}</SelectContent>
                </Select>
                {r.op_status === "DIRTY" && (
                  <Button onClick={() => setStatus(r, "READY")} className="h-9 bg-emerald-600 text-white rounded-xl gap-1 text-xs" data-testid={`mark-ready-${r.number}`}>
                    <Sparkles className="w-3 h-3" /> Готова
                  </Button>
                )}
                {isOwner && <>
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setDlg({ ...r, amenities: (r.amenities || []).join(", ") })}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="outline" size="icon" className="h-9 w-9 text-destructive" onClick={() => del(r.id)}><Trash2 className="w-4 h-4" /></Button>
                </>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dlg?.id ? "Редактиране на стая" : "Нова стая"}</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Номер</Label><Input value={dlg.number} onChange={(e) => setDlg({ ...dlg, number: e.target.value })} data-testid="room-number" /></div>
                <div><Label className="text-xs">Име</Label><Input value={dlg.name} onChange={(e) => setDlg({ ...dlg, name: e.target.value })} data-testid="room-name" /></div>
                <div><Label className="text-xs">Тип</Label>
                  <Select value={dlg.room_type} onValueChange={(v) => setDlg({ ...dlg, room_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="STANDARD">Стандартна</SelectItem><SelectItem value="APARTMENT">Апартамент</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Изглед</Label><Input value={dlg.view} onChange={(e) => setDlg({ ...dlg, view: e.target.value })} /></div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Описание</Label>
                  <button type="button" onClick={genDesc} disabled={aiBusy}
                    className="flex items-center gap-1 text-xs text-gold-dark hover:text-forest disabled:opacity-50" data-testid="ai-gen-room-desc">
                    {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Генерирай с AI
                  </button>
                </div>
                <Textarea value={dlg.description} onChange={(e) => setDlg({ ...dlg, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div><Label className="text-xs">Възр.</Label><Input type="number" value={dlg.max_adults} onChange={(e) => setDlg({ ...dlg, max_adults: e.target.value })} /></div>
                <div><Label className="text-xs">Деца</Label><Input type="number" value={dlg.max_children} onChange={(e) => setDlg({ ...dlg, max_children: e.target.value })} /></div>
                <div><Label className="text-xs">Бебета</Label><Input type="number" value={dlg.max_babies} onChange={(e) => setDlg({ ...dlg, max_babies: e.target.value })} /></div>
                <div><Label className="text-xs">Цена</Label><Input type="number" value={dlg.base_price} onChange={(e) => setDlg({ ...dlg, base_price: e.target.value })} data-testid="room-price" /></div>
              </div>
              <div><Label className="text-xs">Удобства (разделени със запетая)</Label><Input value={dlg.amenities} onChange={(e) => setDlg({ ...dlg, amenities: e.target.value })} /></div>
              <div><Label className="text-xs">Снимка (URL)</Label><Input value={(dlg.photos && dlg.photos[0]) || ""} onChange={(e) => setDlg({ ...dlg, photos: e.target.value ? [e.target.value] : [] })} placeholder="https://…" /></div>
              <div className="flex items-center justify-between"><Label>Активна</Label><Switch checked={dlg.active} onCheckedChange={(v) => setDlg({ ...dlg, active: v })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={save} className="bg-forest text-white rounded-xl" data-testid="save-room">Запази</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
